import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import SupportIcon from "@mui/icons-material/Support";
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { PROOFING_FLOW_DOCUMENTATION_URL, SUPPORT_CONTACT_URL } from "@/lib/constants";
import ThemeSelector from "../ThemeSelector";
import { MenuItemsContainer, StyledAvatar, UserInfoContainer } from "./userAvatarAndMenu.styles";

interface UserAvatarAndMenuProps {
  user: {
    displayName: string;
    mail: string;
  };

  onSignOutClick?: () => void;
}

const UserAvatarAndMenu = ({ user, onSignOutClick }: UserAvatarAndMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const nameArray = user?.displayName?.split(" ") || [];
  const firstLetter = nameArray[0]?.charAt(0)?.toUpperCase();
  const secondLetter = nameArray[1]?.charAt(0)?.toUpperCase() || "";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (callback?: () => void) => {
    handleClose();
    callback?.();
  };

  return (
    <>
      <StyledAvatar
        alt="User Avatar"
        data-testid="user-avatar"
        variant="circular"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        sx={{ cursor: "pointer" }}
      >
        {firstLetter}
        {secondLetter}
      </StyledAvatar>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              mt: 1,
            },
          },
        }}
      >
        <UserInfoContainer>
          <StyledAvatar alt="User Avatar" isDisabled>
            {firstLetter}
            {secondLetter}
          </StyledAvatar>
          <Box>
            <Typography variant="body1" fontWeight={500}>
              {user?.displayName || ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.mail || ""}
            </Typography>
          </Box>
        </UserInfoContainer>

        <Divider />

        <ThemeSelector />

        <MenuItemsContainer>
          <MenuItem
            component="a"
            href={PROOFING_FLOW_DOCUMENTATION_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>
              <LibraryBooksOutlinedIcon />
            </ListItemIcon>
            <ListItemText>Documentation</ListItemText>
          </MenuItem>

          <MenuItem
            component="a"
            href={SUPPORT_CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>
              <SupportIcon />
            </ListItemIcon>
            <ListItemText>Contact Support</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuItemClick(onSignOutClick)}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText>Sign out</ListItemText>
          </MenuItem>
        </MenuItemsContainer>
      </Menu>
    </>
  );
};

export default UserAvatarAndMenu;
